/**
 * ProjectsTable - CRM-style data table for projects
 *
 * Features:
 * - Search by company name
 * - Filters dropdown
 * - Column visibility toggle
 * - Sortable columns (click header to sort)
 * - Clean, professional table UI
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, limit, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  Search,
  Filter,
  Columns3,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  ExternalLink,
  Trash2,
  Copy,
  Pencil,
  Pin,
  PinOff,
  User,
  Users,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '../ui/dropdown-menu';
import type { Project, DealLifecycle, DealStructureType } from '../../hooks/useProjects';
import { EditProjectDialog } from './EditProjectDialog';
import { CreatorBadge } from '../shared/CreatorBadge';
import { useCreatorProfiles } from '../../hooks/useCreatorProfiles';
import {
  DEAL_STAGE_LABELS,
  DEAL_STAGE_ORDER,
  countDealsByStage,
  terminalDealLabel,
  type CanonicalDealStage,
} from '../../lib/dealFunnel';
import {
  STATUS_BADGE_CLASSES,
  dealLifecycleStatusKind,
  dealStageStatusKind,
} from '../../lib/statusTokens';
import { PipelineFunnelStrip } from './PipelineFunnelStrip';

interface ProjectsTableProps {
  projects: Project[];
  loading: boolean;
  maxVisibleRows?: number;
  currentUserId?: string;
  pinnedProjects?: string[];
  onDeleteProject?: (projectId: string) => void;
  onEditProject?: (projectId: string, updates: {
    industry?: string;
    dealStructureType?: DealStructureType;
    metadata?: { dealSource?: string };
  }) => Promise<void>;
  onTogglePin?: (projectId: string) => void;
  /** Render the clickable funnel summary above the table (dashboard). */
  showFunnel?: boolean;
}

// Column configuration
type ColumnKey = 'recentActivity' | 'stage' | 'company' | 'date' | 'creator' | 'dealSource' | 'sector' | 'transactionType';

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  sortable: boolean;
  defaultVisible: boolean;
}

const COLUMNS: ColumnConfig[] = [
  { key: 'recentActivity', label: 'Recent Activity', sortable: true, defaultVisible: true },
  { key: 'stage', label: 'Stage', sortable: true, defaultVisible: true },
  { key: 'company', label: 'Company', sortable: true, defaultVisible: true },
  { key: 'date', label: 'Date Added', sortable: true, defaultVisible: true },
  { key: 'creator', label: 'Creator', sortable: true, defaultVisible: true },
  { key: 'dealSource', label: 'Deal Source', sortable: true, defaultVisible: true },
  { key: 'sector', label: 'Sector', sortable: true, defaultVisible: true },
  { key: 'transactionType', label: 'Transaction Type', sortable: true, defaultVisible: false },
];

// Lifecycle labels (visuals come from the shared status-token register)
const LIFECYCLE_LABELS: Record<DealLifecycle, string> = {
  prospect: 'Prospect',
  active_deal: 'Active Deal',
  portfolio_company: 'Portfolio',
  exited: 'Exited',
  dead: 'Dead',
};

const STAGE_BADGE_BASE = 'inline-flex items-center whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium';

// Sort rank along the funnel; `passed` sorts after the path.
const stageSortRank = (stage: string | undefined): number => {
  const index = DEAL_STAGE_ORDER.indexOf(stage as CanonicalDealStage);
  return index === -1 ? DEAL_STAGE_ORDER.length : index;
};

// Deal Structure Type labels
const DEAL_STRUCTURE_LABELS: Record<DealStructureType, string> = {
  platform: 'Platform',
  add_on: 'Add-on',
  growth: 'Growth',
  buyout: 'Buyout',
  carve_out: 'Carve-out',
  recap: 'Recapitalization',
  other: 'Other',
};

type SortDirection = 'asc' | 'desc' | null;

interface SortState {
  column: ColumnKey | null;
  direction: SortDirection;
}

export function ProjectsTable({ projects, loading, maxVisibleRows, currentUserId, pinnedProjects = [], onDeleteProject, onEditProject, onTogglePin, showFunnel = false }: ProjectsTableProps) {
  const navigate = useNavigate();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortState, setSortState] = useState<SortState>({ column: 'recentActivity', direction: 'desc' });
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    new Set(COLUMNS.filter(c => c.defaultVisible).map(c => c.key))
  );
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [lifecycleFilter, setLifecycleFilter] = useState<DealLifecycle | null>(null);

  // "My Projects" toggle — persisted in localStorage
  const [showMyProjects, setShowMyProjects] = useState<boolean>(() => {
    const stored = localStorage.getItem('corbelShowMyProjects');
    return stored !== null ? stored === 'true' : true;
  });

  // Persist preference
  useEffect(() => {
    localStorage.setItem('corbelShowMyProjects', String(showMyProjects));
  }, [showMyProjects]);

  // Pinned project set for O(1) lookup
  const pinnedSet = useMemo(() => new Set(pinnedProjects), [pinnedProjects]);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  // CIM intermediary data (deal source from CIM analysis)
  const [cimIntermediaries, setCimIntermediaries] = useState<Map<string, string>>(new Map());

  // Recent activity dates per project
  const [recentActivityDates, setRecentActivityDates] = useState<Map<string, Date>>(new Map());
  const creatorProfiles = useCreatorProfiles(projects.map((project) => project.createdBy));

  // Fetch CIM analysis data to get intermediary/deal source
  useEffect(() => {
    const fetchCIMIntermediaries = async () => {
      const intermediaryMap = new Map<string, string>();

      // Fetch in parallel for all projects
      const fetchPromises = projects.map(async (project) => {
        try {
          // Query the cimAnalyses subcollection for this project
          const cimAnalysesRef = collection(db, 'projects', project.id, 'cimAnalyses');
          const cimQuery = query(cimAnalysesRef, orderBy('analyzedAt', 'desc'), limit(1));
          const snapshot = await getDocs(cimQuery);

          if (!snapshot.empty) {
            const cimData = snapshot.docs[0].data();
            // Extract intermediary from processBackground.process.intermediary
            const intermediary = cimData?.processBackground?.process?.intermediary;
            if (intermediary) {
              intermediaryMap.set(project.id, intermediary);
            }
          }
        } catch (error) {
          // Silently handle errors (project may not have CIM analysis yet)
          console.debug(`No CIM analysis found for project ${project.id}`);
        }
      });

      await Promise.all(fetchPromises);
      setCimIntermediaries(intermediaryMap);
    };

    if (projects.length > 0) {
      fetchCIMIntermediaries();
    }
  }, [projects]);

  // Fetch most recent activity for each project
  useEffect(() => {
    const fetchRecentActivities = async () => {
      const activityMap = new Map<string, Date>();

      // Fetch in parallel for all projects
      const fetchPromises = projects.map(async (project) => {
        try {
          // Query the activityLogs subcollection for this project
          const activityLogsRef = collection(db, 'projects', project.id, 'activityLogs');
          const activityQuery = query(activityLogsRef, orderBy('createdAt', 'desc'), limit(1));
          const snapshot = await getDocs(activityQuery);

          if (!snapshot.empty) {
            const activityData = snapshot.docs[0].data();
            // Extract createdAt - handle both Timestamp and Date
            const createdAt = activityData?.createdAt;
            if (createdAt) {
              // Check if it's a Firestore Timestamp or already a Date
              const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
              activityMap.set(project.id, date);
            }
          }
        } catch (error) {
          // Silently handle errors (project may not have activity logs yet)
          console.debug(`No activity logs found for project ${project.id}`);
        }
      });

      await Promise.all(fetchPromises);
      setRecentActivityDates(activityMap);
    };

    if (projects.length > 0) {
      fetchRecentActivities();
    }
  }, [projects]);

  // Helper to get creator name for a project
  const getCreatorName = (project: Project): string => {
    if (!project.createdBy) return '—';
    const creator = creatorProfiles.get(project.createdBy);
    return creator?.displayName || creator?.email || `${project.createdBy.slice(0, 8)}...`;
  };

  const getCreatorProfile = (project: Project) => {
    return project.createdBy ? creatorProfiles.get(project.createdBy) : null;
  };

  // Helper to get recent activity date for a project
  const getRecentActivityDate = (project: Project): Date | undefined => {
    return recentActivityDates.get(project.id);
  };

  // Format relative time for display
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper to get deal source (CIM intermediary takes priority, fallback to metadata)
  const getDealSource = (project: Project): string | undefined => {
    // First check CIM intermediary
    const cimIntermediary = cimIntermediaries.get(project.id);
    if (cimIntermediary) {
      return cimIntermediary;
    }
    // Fallback to metadata.dealSource
    return project.metadata?.dealSource;
  };

  // My/All + search scope — the funnel summarizes this set (before stage filters)
  const scopedProjects = useMemo(() => {
    let result = [...projects];

    // Apply "My Projects" filter
    if (showMyProjects && currentUserId) {
      result = result.filter((p) => p.team?.includes(currentUserId));
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm) ||
          p.companyName.toLowerCase().includes(searchTerm) ||
          p.industry.toLowerCase().includes(searchTerm) ||
          (getDealSource(p)?.toLowerCase().includes(searchTerm) ?? false) ||
          getCreatorName(p).toLowerCase().includes(searchTerm)
      );
    }

    return result;
  }, [projects, searchQuery, cimIntermediaries, creatorProfiles, showMyProjects, currentUserId]);

  const funnelCounts = useMemo(() => countDealsByStage(scopedProjects), [scopedProjects]);

  // Filter and sort projects
  const filteredAndSortedProjects = useMemo(() => {
    let result = [...scopedProjects];

    // Apply stage filter
    if (stageFilter) {
      result = result.filter((p) => p.stage === stageFilter);
    }

    // Apply lifecycle filter
    if (lifecycleFilter) {
      result = result.filter((p) => p.dealLifecycle === lifecycleFilter);
    }

    // Apply sorting
    if (sortState.column && sortState.direction) {
      result.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (sortState.column) {
          case 'recentActivity':
            // Sort by most recent activity date, projects with no activity go to the end
            aVal = getRecentActivityDate(a)?.getTime() || 0;
            bVal = getRecentActivityDate(b)?.getTime() || 0;
            break;
          case 'stage':
            aVal = stageSortRank(a.stage);
            bVal = stageSortRank(b.stage);
            break;
          case 'company':
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case 'date':
            aVal = a.createdAt.getTime();
            bVal = b.createdAt.getTime();
            break;
          case 'creator':
            aVal = getCreatorName(a).toLowerCase();
            bVal = getCreatorName(b).toLowerCase();
            break;
          case 'dealSource':
            aVal = getDealSource(a)?.toLowerCase() || '';
            bVal = getDealSource(b)?.toLowerCase() || '';
            break;
          case 'sector':
            aVal = a.industry.toLowerCase();
            bVal = b.industry.toLowerCase();
            break;
          case 'transactionType':
            aVal = a.dealStructureType || '';
            bVal = b.dealStructureType || '';
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortState.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortState.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Pinned projects always sort to top (stable — preserves existing sort within groups)
    if (pinnedSet.size > 0) {
      result.sort((a, b) => {
        const aPinned = pinnedSet.has(a.id) ? 1 : 0;
        const bPinned = pinnedSet.has(b.id) ? 1 : 0;
        return bPinned - aPinned; // pinned first
      });
    }

    return result;
  }, [scopedProjects, sortState, stageFilter, lifecycleFilter, recentActivityDates, creatorProfiles, pinnedSet]);

  // Handle column header click for sorting
  const handleSort = (column: ColumnKey) => {
    setSortState((prev) => {
      if (prev.column === column) {
        // Cycle: asc -> desc -> null -> asc
        if (prev.direction === 'asc') return { column, direction: 'desc' };
        if (prev.direction === 'desc') return { column: null, direction: null };
        return { column, direction: 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  // Toggle column visibility
  const toggleColumn = (column: ColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(column)) {
        next.delete(column);
      } else {
        next.add(column);
      }
      return next;
    });
  };

  // Render sort indicator
  const renderSortIndicator = (column: ColumnKey) => {
    if (sortState.column !== column) {
      return <ArrowUpDown className="ml-2 h-4 w-4 text-muted-foreground/50" />;
    }
    if (sortState.direction === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4 text-primary" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4 text-primary" />;
  };

  // Render the merged Stage cell: one canonical funnel chip, plus the
  // lifecycle disposition only when it adds information beyond the stage.
  const renderStageBadge = (project: Project) => {
    const lifecycle = project.dealLifecycle;
    const stage = project.stage;

    // Out of the pipeline → a single disposition chip (Passed / Exited)
    const terminal = terminalDealLabel(stage, lifecycle);
    if (terminal) {
      const kind = lifecycle === 'exited' ? dealLifecycleStatusKind('exited') : dealStageStatusKind('passed');
      return <span className={`${STAGE_BADGE_BASE} ${STATUS_BADGE_CLASSES[kind]}`}>{terminal}</span>;
    }

    const stageChip = stage && DEAL_STAGE_LABELS[stage] ? (
      <span className={`${STAGE_BADGE_BASE} ${STATUS_BADGE_CLASSES[dealStageStatusKind(stage)]}`}>
        {DEAL_STAGE_LABELS[stage]}
      </span>
    ) : null;

    // Portfolio companies keep their rollup chip; for a closed deal the stage
    // chip is implied by it, so show the more meaningful label alone.
    if (lifecycle === 'portfolio_company') {
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`${STAGE_BADGE_BASE} ${STATUS_BADGE_CLASSES[dealLifecycleStatusKind(lifecycle)]}`}>
            {LIFECYCLE_LABELS[lifecycle]}
          </span>
          {stage !== 'closed' && stageChip}
        </div>
      );
    }

    return stageChip ?? <span className="text-muted-foreground text-xs">—</span>;
  };

  // Get unique stages and lifecycles for filters
  const uniqueStages = useMemo(() => {
    return [...new Set(projects.map((p) => p.stage).filter(Boolean))];
  }, [projects]);

  const uniqueLifecycles = useMemo(() => {
    return [...new Set(projects.map((p) => p.dealLifecycle).filter(Boolean))] as DealLifecycle[];
  }, [projects]);

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="h-10 w-64 bg-muted animate-pulse rounded-md" />
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-muted animate-pulse rounded-md" />
            <div className="h-10 w-24 bg-muted animate-pulse rounded-md" />
          </div>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <div className="h-12 bg-muted/30 border-b" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 border-b last:border-0 animate-pulse bg-muted/10" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Funnel summary — counts reflect the My/All + search scope */}
      {showFunnel && (
        <PipelineFunnelStrip
          counts={funnelCounts}
          activeStage={stageFilter}
          onStageSelect={(stage) => setStageFilter(stage)}
        />
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Left: My/All toggle + Search */}
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
          {/* My Projects / All Projects toggle */}
          {currentUserId && (
            <div className="grid w-full grid-cols-2 rounded-lg border border-border bg-muted/30 p-0.5 text-sm sm:inline-flex sm:w-auto sm:items-center" role="group" aria-label="Project filter">
              <button
                onClick={() => setShowMyProjects(true)}
                aria-pressed={showMyProjects}
                className={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                  showMyProjects
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <User className="h-3.5 w-3.5" />
                My Projects
              </button>
              <button
                onClick={() => setShowMyProjects(false)}
                aria-pressed={!showMyProjects}
                className={`inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                  !showMyProjects
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Users className="h-3.5 w-3.5" />
                All Projects
              </button>
            </div>
          )}

          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects by company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10"
            />
          </div>
        </div>

        {/* Filters and Column Toggle */}
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
          {/* Filters Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-10 flex-1 sm:flex-none">
                <Filter className="mr-2 h-4 w-4" />
                Filters
                {(stageFilter || lifecycleFilter) && (
                  <span className="ml-2 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                    {(stageFilter ? 1 : 0) + (lifecycleFilter ? 1 : 0)}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Filter by Stage</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={stageFilter === null}
                onCheckedChange={() => setStageFilter(null)}
              >
                All Stages
              </DropdownMenuCheckboxItem>
              {uniqueStages.map((stage) => (
                <DropdownMenuCheckboxItem
                  key={stage}
                  checked={stageFilter === stage}
                  onCheckedChange={() => setStageFilter(stageFilter === stage ? null : stage)}
                >
                  {DEAL_STAGE_LABELS[stage] || stage}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Filter by Lifecycle</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={lifecycleFilter === null}
                onCheckedChange={() => setLifecycleFilter(null)}
              >
                All Lifecycles
              </DropdownMenuCheckboxItem>
              {uniqueLifecycles.map((lifecycle) => (
                <DropdownMenuCheckboxItem
                  key={lifecycle}
                  checked={lifecycleFilter === lifecycle}
                  onCheckedChange={() => setLifecycleFilter(lifecycleFilter === lifecycle ? null : lifecycle)}
                >
                  {LIFECYCLE_LABELS[lifecycle] || lifecycle}
                </DropdownMenuCheckboxItem>
              ))}
              {(stageFilter || lifecycleFilter) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setStageFilter(null);
                      setLifecycleFilter(null);
                    }}
                    className="text-destructive"
                  >
                    Clear all filters
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Columns Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-10 flex-1 sm:flex-none">
                <Columns3 className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={visibleColumns.has(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {filteredAndSortedProjects.length} {filteredAndSortedProjects.length === 1 ? 'project' : 'projects'}
        {searchQuery && ` matching "${searchQuery}"`}
      </div>

      {/* Table */}
      <div className="border rounded-lg bg-card max-lg:overflow-x-auto lg:overflow-hidden">
        <div className={maxVisibleRows ? 'max-h-[600px] overflow-auto' : ''}>
        <Table className="min-w-[900px] lg:min-w-0">
          <TableHeader className={maxVisibleRows ? 'sticky top-0 z-10 bg-card' : ''}>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.has('recentActivity') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('recentActivity')}
                >
                  <div className="flex items-center">
                    Recent Activity
                    {renderSortIndicator('recentActivity')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('stage') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('stage')}
                >
                  <div className="flex items-center">
                    Stage
                    {renderSortIndicator('stage')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('company') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('company')}
                >
                  <div className="flex items-center">
                    Company
                    {renderSortIndicator('company')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('date') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center">
                    Date Added
                    {renderSortIndicator('date')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('creator') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('creator')}
                >
                  <div className="flex items-center">
                    Creator
                    {renderSortIndicator('creator')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('dealSource') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('dealSource')}
                >
                  <div className="flex items-center">
                    Deal Source
                    {renderSortIndicator('dealSource')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('sector') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('sector')}
                >
                  <div className="flex items-center">
                    Sector
                    {renderSortIndicator('sector')}
                  </div>
                </TableHead>
              )}
              {visibleColumns.has('transactionType') && (
                <TableHead
                  className="cursor-pointer select-none"
                  onClick={() => handleSort('transactionType')}
                >
                  <div className="flex items-center">
                    Transaction Type
                    {renderSortIndicator('transactionType')}
                  </div>
                </TableHead>
              )}
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedProjects.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.size + 1}
                  className="h-32 text-center text-muted-foreground"
                >
                  {searchQuery || stageFilter || lifecycleFilter
                    ? 'No projects match your filters'
                    : 'No projects yet'}
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedProjects.map((project) => (
                <TableRow
                  key={project.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/project/${project.id}`)}
                >
                  {visibleColumns.has('recentActivity') && (
                    <TableCell>
                      {getRecentActivityDate(project) ? (
                        <span className="text-muted-foreground text-sm">
                          {formatRelativeTime(getRecentActivityDate(project)!)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50 text-sm">—</span>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.has('stage') && (
                    <TableCell>{renderStageBadge(project)}</TableCell>
                  )}
                  {visibleColumns.has('company') && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {pinnedSet.has(project.id) && (
                          <Pin className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                        )}
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground hover:text-primary transition-colors">
                            {project.name}
                          </span>
                          {project.companyName && project.companyName !== project.name && (
                            <span className="text-xs text-muted-foreground">
                              {project.companyName}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  )}
                  {visibleColumns.has('date') && (
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {project.createdAt.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </TableCell>
                  )}
                  {visibleColumns.has('creator') && (
                    <TableCell>
                      <CreatorBadge
                        creator={getCreatorProfile(project)}
                        fallbackName={getCreatorName(project)}
                        avatarClassName="h-6 w-6"
                        nameClassName="max-w-[120px]"
                      />
                    </TableCell>
                  )}
                  {visibleColumns.has('dealSource') && (
                    <TableCell>
                      {getDealSource(project) ? (
                        <span
                          className="block max-w-[220px] truncate text-muted-foreground"
                          title={getDealSource(project)}
                        >
                          {getDealSource(project)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.has('sector') && (
                    <TableCell>
                      <span className="text-muted-foreground">{project.industry}</span>
                    </TableCell>
                  )}
                  {visibleColumns.has('transactionType') && (
                    <TableCell>
                      {project.dealStructureType ? (
                        <span className="text-muted-foreground">
                          {DEAL_STRUCTURE_LABELS[project.dealStructureType] || project.dealStructureType}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/project/${project.id}`);
                          }}
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Open Project
                        </DropdownMenuItem>
                        {onTogglePin && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onTogglePin(project.id);
                            }}
                          >
                            {pinnedSet.has(project.id) ? (
                              <>
                                <PinOff className="mr-2 h-4 w-4" />
                                Unpin
                              </>
                            ) : (
                              <>
                                <Pin className="mr-2 h-4 w-4" />
                                Pin to Top
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                        {onEditProject && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProject(project);
                              setEditDialogOpen(true);
                            }}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(project.id);
                          }}
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copy ID
                        </DropdownMenuItem>
                        {onDeleteProject && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`Delete "${project.name}"? This cannot be undone.`)) {
                                  onDeleteProject(project.id);
                                }
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Edit Project Dialog */}
      {onEditProject && (
        <EditProjectDialog
          project={editingProject}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditingProject(null);
          }}
          onSave={onEditProject}
        />
      )}
    </div>
  );
}
